import React from 'react';
import { Star, Flame, Award, Clock, Leaf } from 'lucide-react';
import { MenuItem } from '../../services/dataService';
import { getCategoryIcon } from './publicMenuCategories';
import { isItemFeatured, isItemPopular } from './publicMenuItemUtils';
import MenuItemCartControls from './MenuItemCartControls';
import { CartItem } from './publicMenuTypes';

type Props = {
  groupedItems: Record<string, MenuItem[]>;
  isTableMode: boolean;
  customerName: string;
  cartItems: CartItem[];
  getItemQuantityInCart: (menuItemId: string) => number;
  onAddToCart: (item: MenuItem) => void;
  onIncrementCart: (cartItemId: string) => void;
  onDecrementCart: (cartItemId: string) => void;
};

const ImageMenuGrid: React.FC<Props> = ({
  groupedItems,
  isTableMode,
  customerName,
  cartItems,
  getItemQuantityInCart,
  onAddToCart,
  onIncrementCart,
  onDecrementCart,
}) => {
  return (
    <div className="space-y-8">
      {Object.entries(groupedItems).map(([category, items]) => {
        const CategoryIcon = getCategoryIcon(category);
        return (
          <section key={category} className="scroll-mt-36">
            <div className="mb-3 flex items-center gap-2">
              <CategoryIcon className="h-5 w-5 text-amber-600" />
              <h2 className="text-base font-bold text-zinc-900">{category}</h2>
            </div>
            <div className="grid grid-cols-2 gap-2.5 tablet:grid-cols-3 desktop:grid-cols-4">
              {items.map((item) => {
                const featured = isItemFeatured(item);
                const popular = isItemPopular(item);
                const qty = item.id ? getItemQuantityInCart(item.id) : 0;

                return (
                  <article
                    key={item.id}
                    className={`flex flex-col overflow-hidden rounded-xl border border-zinc-200/90 bg-white shadow-sm ${
                      featured ? 'ring-1 ring-amber-200' : ''
                    }`}
                  >
                    <div className="relative aspect-[4/3] bg-gradient-to-br from-amber-50 to-orange-50">
                      {item.image ? (
                        <img
                          src={item.image}
                          alt=""
                          className="h-full w-full object-cover"
                          loading="lazy"
                          decoding="async"
                          onError={(e) => {
                            (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
                          }}
                        />
                      ) : null}
                      <div className="pointer-events-none absolute left-1 top-1 flex flex-wrap gap-0.5">
                        {item.isTodaysSpecial && (
                          <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-500 px-1.5 py-0.5 text-[9px] font-bold text-white shadow">
                            <Star className="h-2.5 w-2.5" />
                            Today
                          </span>
                        )}
                        {item.isSpecialOffer && !item.isTodaysSpecial && (
                          <span className="inline-flex items-center gap-0.5 rounded-full bg-rose-500 px-1.5 py-0.5 text-[9px] font-bold text-white shadow">
                            <Flame className="h-2.5 w-2.5" />
                            Offer
                          </span>
                        )}
                        {item.isFeatured && !item.isSpecialOffer && !item.isTodaysSpecial && (
                          <span className="inline-flex items-center gap-0.5 rounded-full bg-orange-500 px-1.5 py-0.5 text-[9px] font-bold text-white shadow">
                            <Star className="h-2.5 w-2.5" />
                            Featured
                          </span>
                        )}
                        {popular && !featured && (
                          <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-500 px-1.5 py-0.5 text-[9px] font-bold text-white shadow">
                            <Award className="h-2.5 w-2.5" />
                            Popular
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-1 flex-col gap-1 p-2">
                      <h3 className="line-clamp-2 text-xs font-bold leading-tight text-zinc-900 tablet:text-sm">
                        {item.name}
                      </h3>
                      {item.description && (
                        <p className="line-clamp-2 text-[10px] leading-snug text-zinc-500 tablet:text-xs">{item.description}</p>
                      )}
                      <div className="mt-auto flex flex-wrap items-center gap-1">
                        <span className="text-sm font-bold text-amber-600">₹{item.price.toFixed(2)}</span>
                        {item.originalPrice && item.originalPrice > item.price && (
                          <span className="text-[10px] text-zinc-400 line-through">₹{item.originalPrice.toFixed(2)}</span>
                        )}
                        {item.preparationTime > 0 && (
                          <span className="ml-auto inline-flex items-center gap-0.5 text-[10px] text-zinc-400">
                            <Clock className="h-3 w-3" />
                            {item.preparationTime}m
                          </span>
                        )}
                      </div>
                      {item.dietaryInfo && item.dietaryInfo.length > 0 && (
                        <div className="flex flex-wrap gap-0.5">
                          {item.dietaryInfo.slice(0, 2).map((info) => (
                            <span
                              key={info}
                              className="inline-flex items-center gap-0.5 rounded bg-green-50 px-1 py-0.5 text-[9px] font-medium text-green-800"
                            >
                              <Leaf className="h-2 w-2" />
                              {info}
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="pt-1">
                        <MenuItemCartControls
                          item={item}
                          isTableMode={isTableMode}
                          customerName={customerName}
                          quantityInCart={qty}
                          cartItems={cartItems}
                          onAdd={onAddToCart}
                          onIncrement={(id) => onIncrementCart(id)}
                          onDecrement={(id) => onDecrementCart(id)}
                          compact
                          fullWidth
                        />
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default ImageMenuGrid;
